// src/features/billing/InvoiceNumberingTab.tsx
// Invoice numbering schemes: how invoice numbers are generated (e.g. INV-2026-001)
// for a scope entity. Tenant-wide admin config, same placement reasoning as
// BillingPoliciesTab.
//
// Covers: ListInvoiceNumberingSchemes, CreateInvoiceNumberingScheme,
// GetInvoiceNumberingScheme (edit loads full record), UpdateInvoiceNumberingScheme,
// DeleteInvoiceNumberingScheme.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useBillingInvoiceNumbering, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/form';
import type { InvoiceNumberingScheme, InvoiceNumberingPattern } from '@core/types/billing';
import { errMessage } from './format';

interface SchemeForm {
  name: string;
  description: string;
  scope_entity_id: string;
  scope_entity_type: string;
  prefix: string;
  separator: string;
  counter_digits: string;
  format_template: string;
  reset_strategy: string;
  scope: string;
  patternYear: boolean;
  patternMonth: boolean;
  patternQuarter: boolean;
  patternCounter: boolean;
}

const EMPTY: SchemeForm = {
  name: '', description: '', scope_entity_id: '', scope_entity_type: 'tenant',
  prefix: 'INV', separator: '-', counter_digits: '3', format_template: '{prefix}{sep}{year}{sep}{counter}',
  reset_strategy: 'INVOICE_NUMBERING_RESET_STRATEGY_YEARLY', scope: 'tenant',
  patternYear: true, patternMonth: false, patternQuarter: false, patternCounter: true,
};

const RESET_OPTIONS = [
  { value: 'INVOICE_NUMBERING_RESET_STRATEGY_NEVER', labelKey: 'billing.numbering.resetNever' },
  { value: 'INVOICE_NUMBERING_RESET_STRATEGY_YEARLY', labelKey: 'billing.numbering.resetYearly' },
  { value: 'INVOICE_NUMBERING_RESET_STRATEGY_QUARTERLY', labelKey: 'billing.numbering.resetQuarterly' },
  { value: 'INVOICE_NUMBERING_RESET_STRATEGY_MONTHLY', labelKey: 'billing.numbering.resetMonthly' },
];

function buildPattern(form: SchemeForm): InvoiceNumberingPattern[] {
  const pattern: InvoiceNumberingPattern[] = [];
  if (form.patternYear) pattern.push('INVOICE_NUMBERING_PATTERN_YEAR');
  if (form.patternMonth) pattern.push('INVOICE_NUMBERING_PATTERN_MONTH');
  if (form.patternQuarter) pattern.push('INVOICE_NUMBERING_PATTERN_QUARTER');
  if (form.patternCounter) pattern.push('INVOICE_NUMBERING_PATTERN_COUNTER');
  return pattern;
}

export const InvoiceNumberingTab = observer(() => {
  const store = useBillingInvoiceNumbering();
  const { t } = useI18n();
  const [editing, setEditing] = useState<InvoiceNumberingScheme | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<SchemeForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<InvoiceNumberingScheme | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    store.ListInvoiceNumberingSchemes({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };

  const fillForm = (s: InvoiceNumberingScheme) => setForm({
    name: s.name ?? '', description: s.description ?? '',
    scope_entity_id: s.scope_entity?.id ?? '', scope_entity_type: s.scope_entity_type ?? 'tenant',
    prefix: s.prefix ?? '', separator: s.separator ?? '-', counter_digits: String(s.counter_digits ?? 3),
    format_template: s.format_template ?? '', reset_strategy: s.reset_strategy ?? 'INVOICE_NUMBERING_RESET_STRATEGY_YEARLY',
    scope: s.scope ?? 'tenant',
    patternYear: s.pattern?.includes('INVOICE_NUMBERING_PATTERN_YEAR') ?? false,
    patternMonth: s.pattern?.includes('INVOICE_NUMBERING_PATTERN_MONTH') ?? false,
    patternQuarter: s.pattern?.includes('INVOICE_NUMBERING_PATTERN_QUARTER') ?? false,
    patternCounter: s.pattern?.includes('INVOICE_NUMBERING_PATTERN_COUNTER') ?? true,
  });

  const openEdit = async (s: InvoiceNumberingScheme) => {
    if (!s.entity?.id) return;
    try {
      const full = await store.GetInvoiceNumberingScheme(s.entity.id);
      setEditing(full);
      fillForm(full);
    } catch {
      setEditing(s);
      fillForm(s);
    }
    setFormOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim() || (!editing && !form.scope_entity_id.trim())) return;
    setError(null);
    try {
      const pattern = buildPattern(form);
      if (editing?.entity?.id) {
        await store.UpdateInvoiceNumberingScheme(editing.entity.id, {
          scheme: {
            ...editing,
            name: form.name,
            description: form.description,
            prefix: form.prefix,
            separator: form.separator,
            pattern,
            counter_digits: Number(form.counter_digits) || 0,
            format_template: form.format_template,
            reset_strategy: form.reset_strategy as any,
            scope: form.scope,
          } as any,
          field_mask: [],
        } as any);
      } else {
        await store.CreateInvoiceNumberingScheme({
          entity: {} as any,
          name: form.name,
          description: form.description,
          scope_entity: { id: form.scope_entity_id } as any,
          scope_entity_type: form.scope_entity_type,
          prefix: form.prefix,
          separator: form.separator,
          pattern,
          counter_digits: Number(form.counter_digits) || 0,
          format_template: form.format_template,
          reset_strategy: form.reset_strategy as any,
          scope: form.scope,
        } as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('billing.numbering.saveError')));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeleteInvoiceNumberingScheme(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('billing.numbering.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('billing.numbering.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('billing.numbering.format')}</TableHead>
            <TableHead>{t('billing.numbering.resetStrategy')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('billing.numbering.empty')} />
          ) : (
            store.items.map((s) => (
              <TableRow key={s.entity?.id}>
                <TableCell>{s.name}</TableCell>
                <TableCell className="font-mono text-xs">{s.prefix}{s.separator}{'Y'.repeat(4)}{s.separator}{'0'.repeat(s.counter_digits || 3)}</TableCell>
                <TableCell>{RESET_OPTIONS.find((o) => o.value === s.reset_strategy)?.labelKey ? t(RESET_OPTIONS.find((o) => o.value === s.reset_strategy)!.labelKey) : '--'}</TableCell>
                <TableCell>{s.is_active ? <Badge variant="success" size="sm">{t('common.active')}</Badge> : <Badge variant="secondary" size="sm">{t('common.inactive')}</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(s)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={submit}
        submitLabel={editing ? t('common.saveChanges') : t('common.create')}
        canSubmit={!!form.name.trim() && (!!editing || !!form.scope_entity_id.trim())}
        isSubmitting={store.isSaving}
        dirty={!!(form.name.trim() || form.description.trim())}
        aria-label={editing ? t('billing.numbering.edit') : t('billing.numbering.create')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <h3 className="text-base font-semibold text-foreground">{editing ? t('billing.numbering.edit') : t('billing.numbering.create')}</h3>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
          <Input label={t('common.description')} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          {!editing && (
            <div className="grid grid-cols-2 gap-3">
              <Input label={t('billing.numbering.scopeEntityId')} value={form.scope_entity_id} onChange={(e) => setForm((p) => ({ ...p, scope_entity_id: e.target.value }))} required />
              <Input label={t('billing.numbering.scopeEntityType')} value={form.scope_entity_type} onChange={(e) => setForm((p) => ({ ...p, scope_entity_type: e.target.value }))} />
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Input label={t('billing.numbering.prefix')} value={form.prefix} onChange={(e) => setForm((p) => ({ ...p, prefix: e.target.value }))} className="font-mono" />
            <Input label={t('billing.numbering.separator')} value={form.separator} onChange={(e) => setForm((p) => ({ ...p, separator: e.target.value }))} className="font-mono" />
            <Input
              label={t('billing.numbering.counterDigits')}
              type="text"
              inputMode="numeric"
              value={form.counter_digits}
              onChange={(e) => setForm((p) => ({ ...p, counter_digits: e.target.value.replace(/[^0-9]/g, '') }))}
              placeholder="3"
            />
          </div>
          <Input label={t('billing.numbering.formatTemplate')} value={form.format_template} onChange={(e) => setForm((p) => ({ ...p, format_template: e.target.value }))} placeholder="{prefix}{sep}{year}{sep}{counter}" className="font-mono" />
          <Select label={t('billing.numbering.resetStrategy')} value={form.reset_strategy} onChange={(e) => setForm((p) => ({ ...p, reset_strategy: e.target.value }))}>
            {RESET_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>)}
          </Select>
          <div>
            <span className="block text-sm font-medium text-foreground mb-1">{t('billing.numbering.pattern')}</span>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.patternYear} onChange={(e) => setForm((p) => ({ ...p, patternYear: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                {t('billing.numbering.patternYear')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.patternMonth} onChange={(e) => setForm((p) => ({ ...p, patternMonth: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                {t('billing.numbering.patternMonth')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.patternQuarter} onChange={(e) => setForm((p) => ({ ...p, patternQuarter: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                {t('billing.numbering.patternQuarter')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={form.patternCounter} onChange={(e) => setForm((p) => ({ ...p, patternCounter: e.target.checked }))} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
                {t('billing.numbering.patternCounter')}
              </label>
            </div>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('billing.numbering.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default InvoiceNumberingTab;
